import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Divider,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ScheduleToggle } from '@/components/ScheduleToggle';
import { catalogueApi } from '@/features/catalogue/catalogue.api';
import { CostTag } from '../../ai/ui/CostTag';
import { ComposePostCard } from './ComposePostCard';
import {
  contentApi,
  type ContentCampaign,
  type ContentItem,
  type ContentStatus,
} from '../content.api';

const STATUS_COLORS: Record<ContentStatus, string> = {
  draft: 'default',
  approved: 'green',
  scheduled: 'blue',
  published: 'purple',
};

export function ContentTab({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['content', orgId, projectId] as const;
  const [occasion, setOccasion] = useState<string | undefined>(undefined);
  const [campaignProductIds, setCampaignProductIds] = useState<string[]>([]);
  const [campaignBrand, setCampaignBrand] = useState<string | undefined>(undefined);

  const query = useQuery({ queryKey: key, queryFn: () => contentApi.list(orgId, projectId) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const campaignsKey = ['content-campaigns', orgId, projectId] as const;
  const campaigns = useQuery({
    queryKey: campaignsKey,
    queryFn: () => contentApi.listCampaigns(orgId, projectId),
  });
  const occasions = useQuery({
    queryKey: ['content-occasions', orgId, projectId] as const,
    queryFn: () => contentApi.occasions(orgId, projectId),
    enabled: canEdit,
  });
  const invalidateCampaigns = () => queryClient.invalidateQueries({ queryKey: campaignsKey });

  const products = useQuery({
    queryKey: ['products-all', orgId, projectId] as const,
    queryFn: () => catalogueApi.list(orgId, projectId, 1, 100),
    enabled: canEdit,
  });
  const brands = useQuery({
    queryKey: ['product-brands', orgId, projectId] as const,
    queryFn: () => catalogueApi.brands(orgId, projectId),
    enabled: canEdit,
  });

  const planCampaign = useMutation({
    mutationFn: () =>
      contentApi.planCampaign(orgId, projectId, occasion, campaignProductIds, campaignBrand),
    onSuccess: () => {
      message.success(
        occasion || campaignProductIds.length > 0 || campaignBrand
          ? 'Campaign planning queued — posts appear once the worker runs'
          : 'Planning the next upcoming occasion — check back shortly',
      );
      setTimeout(() => {
        void invalidate();
        void invalidateCampaigns();
      }, 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to plan campaign'),
  });

  const settingsKey = ['content-settings', orgId, projectId] as const;
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => contentApi.getSettings(orgId, projectId),
  });
  const autoPublishMut = useMutation({
    mutationFn: (v: boolean) => contentApi.setAutoPublish(orgId, projectId, v),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKey, data);
      message.success(
        data.autoPublish
          ? 'Autonomous publishing ON — posts go live without approval'
          : 'Auto-publish off — posts are drafts',
      );
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update setting'),
  });

  const generate = useMutation({
    mutationFn: () => contentApi.generate(orgId, projectId),
    onSuccess: () => {
      message.success('Generation queued — drafts appear once the worker runs');
      setTimeout(() => void invalidate(), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to generate'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContentStatus }) =>
      contentApi.updateStatus(orgId, projectId, id, status),
    onSuccess: () => {
      void invalidate();
      message.success('Updated');
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => contentApi.publish(orgId, projectId, id),
    onSuccess: (res) => {
      void invalidate();
      const channels = res.refs.map((r) => r.channel).join(' + ');
      message.success(channels ? `Published to ${channels}` : 'Published');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to publish'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => contentApi.remove(orgId, projectId, id),
    onSuccess: () => {
      void invalidate();
      message.success('Deleted');
    },
  });

  const [editItem, setEditItem] = useState<ContentItem | null>(null);
  const [editForm, setEditForm] = useState({ title: '', body: '', cta: '', hashtags: '' });
  const openEdit = (item: ContentItem) => {
    setEditItem(item);
    setEditForm({
      title: item.title,
      body: item.body,
      cta: item.cta,
      hashtags: item.hashtags.join(', '),
    });
  };
  const editText = useMutation({
    mutationFn: () =>
      contentApi.editText(orgId, projectId, editItem?.id ?? '', {
        title: editForm.title,
        body: editForm.body,
        cta: editForm.cta,
        hashtags: editForm.hashtags
          .split(',')
          .map((h) => h.trim().replace(/^#/, ''))
          .filter(Boolean),
      }),
    onSuccess: () => {
      void invalidate();
      setEditItem(null);
      message.success('Post updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update'),
  });

  const [regenFor, setRegenFor] = useState<string | null>(null);
  const [regenText, setRegenText] = useState('');
  const regenImage = useMutation({
    mutationFn: ({ id, instruction }: { id: string; instruction?: string }) =>
      contentApi.regenerateImage(orgId, projectId, id, instruction),
    onSuccess: () => {
      void invalidate();
      setRegenFor(null);
      setRegenText('');
      message.success('Image regenerated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to regenerate image'),
  });

  const items = query.data ?? [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {canEdit ? (
        <Space>
          <Button type="primary" onClick={() => generate.mutate()} loading={generate.isPending}>
            Generate content now
          </Button>
          <Button onClick={() => invalidate()}>Refresh</Button>
          <ScheduleToggle
            orgId={orgId}
            projectId={projectId}
            kind="generate_content"
            label="Autonomous daily"
            hint="When on, the Content Strategist generates a fresh batch of posts every day."
            canEdit={canEdit}
          />
          <ScheduleToggle
            orgId={orgId}
            projectId={projectId}
            kind="week_ahead"
            label="Week-ahead autopilot"
            hint="Keeps at least 7 days of scheduled posts ahead and revises upcoming posts when live performance suggests improvements."
            canEdit={canEdit}
          />
          <Tooltip title="When on, generated posts are published immediately with no approval step.">
            <Space>
              <Switch
                checked={settings.data?.autoPublish ?? false}
                loading={settings.isLoading || autoPublishMut.isPending}
                onChange={(v) => autoPublishMut.mutate(v)}
              />
              Auto-publish (no approval)
            </Space>
          </Tooltip>
        </Space>
      ) : null}

      {canEdit ? (
        <ComposePostCard orgId={orgId} projectId={projectId} onChanged={() => void invalidate()} />
      ) : null}

      <Card size="small" title="Themed campaigns">
        {canEdit ? (
          <Space wrap style={{ marginBottom: campaigns.data?.length ? 12 : 0 }}>
            <Select
              allowClear
              placeholder="Auto-detect next occasion"
              style={{ minWidth: 220 }}
              value={occasion}
              onChange={(v) => setOccasion(v)}
              options={(occasions.data ?? []).map((o) => ({ value: o.key, label: o.name }))}
              loading={occasions.isLoading}
            />
            <Select<string>
              allowClear
              showSearch
              placeholder="Whole brand (optional)"
              style={{ minWidth: 200 }}
              value={campaignBrand ?? null}
              onChange={(v) => setCampaignBrand(v ?? undefined)}
              options={(brands.data ?? []).map((b) => ({ value: b, label: b }))}
              loading={brands.isLoading}
              notFoundContent="No brands in the catalogue"
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="All products (or pick a product group)"
              style={{ minWidth: 280 }}
              value={campaignProductIds}
              onChange={setCampaignProductIds}
              maxTagCount={2}
              options={(products.data?.items ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              loading={products.isLoading}
            />
            <Button
              type="primary"
              onClick={() => planCampaign.mutate()}
              loading={planCampaign.isPending}
            >
              Plan a campaign
            </Button>
            <ScheduleToggle
              orgId={orgId}
              projectId={projectId}
              kind="plan_campaign"
              label="Autonomous campaigns weekly"
              hint="The strategist scans the calendar weekly and plans the next occasion automatically."
              canEdit={canEdit}
            />
          </Space>
        ) : null}

        {campaigns.isLoading ? (
          <Spin />
        ) : (campaigns.data?.length ?? 0) === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No campaigns yet — plan one for an upcoming occasion"
          />
        ) : (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {(campaigns.data ?? []).map((c: ContentCampaign) => (
              <Card
                key={c.id}
                size="small"
                type="inner"
                title={
                  <Space>
                    {c.name}
                    {c.occasionKey ? <Tag color="magenta">{c.occasionKey}</Tag> : <Tag>custom</Tag>}
                    <Tag color="blue">{c.postCount} posts</Tag>
                    <Tag color={c.source === 'llm' ? 'gold' : 'default'}>{c.source}</Tag>
                  </Space>
                }
              >
                <Typography.Text type="secondary">
                  {new Date(c.startDate).toLocaleDateString()} –{' '}
                  {new Date(c.endDate).toLocaleDateString()}
                </Typography.Text>
                {c.rationale ? <p style={{ margin: '6px 0 0' }}>{c.rationale}</p> : null}
              </Card>
            ))}
          </Space>
        )}
      </Card>

      <Divider style={{ margin: 0 }} orientation="left" plain>
        Posts
      </Divider>

      {query.isLoading ? (
        <Spin />
      ) : items.length === 0 ? (
        <Empty description="No content yet — generate some" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {items.map((item: ContentItem) => (
            <Card key={item.id} size="small">
              {/* Responsive header: title+tags and actions wrap independently
                  instead of colliding/truncating on narrow screens. */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <Space wrap size={[4, 4]} style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{item.title}</span>
                  <Tag color={STATUS_COLORS[item.status]}>{item.status}</Tag>
                  <Tag>{item.type}</Tag>
                  <CostTag cost={item.cost} />
                  <Tag color={item.source === 'llm' ? 'gold' : 'default'}>{item.source}</Tag>
                  {item.scheduledFor && item.status !== 'published' ? (
                    <Tag color="cyan">
                      {item.status === 'scheduled' ? 'auto-publishes' : 'planned for'}{' '}
                      {new Date(item.scheduledFor).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Tag>
                  ) : null}
                  {item.publishedAt ? (
                    <Tag color="purple">
                      published{' '}
                      {new Date(item.publishedAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Tag>
                  ) : null}
                </Space>
                {canEdit ? (
                  <Space wrap size={[4, 4]}>
                    <Button size="small" onClick={() => openEdit(item)}>
                      Edit
                    </Button>
                    {item.status === 'draft' ? (
                      <Button
                        size="small"
                        onClick={() => setStatus.mutate({ id: item.id, status: 'approved' })}
                      >
                        Approve
                      </Button>
                    ) : null}
                    {item.status !== 'published' ? (
                      <Button
                        size="small"
                        type="primary"
                        loading={publish.isPending && publish.variables === item.id}
                        onClick={() => publish.mutate(item.id)}
                      >
                        Publish now
                      </Button>
                    ) : null}
                    <Popconfirm
                      title="Delete this content?"
                      onConfirm={() => remove.mutate(item.id)}
                    >
                      <Button size="small" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ) : null}
              </div>
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  style={{
                    width: '100%',
                    maxWidth: 320,
                    borderRadius: 8,
                    marginBottom: 8,
                    display: 'block',
                  }}
                />
              ) : null}
              {canEdit ? (
                <div style={{ marginBottom: 8 }}>
                  <Popover
                    trigger="click"
                    open={regenFor === item.id}
                    onOpenChange={(open) => {
                      setRegenFor(open ? item.id : null);
                      if (!open) setRegenText('');
                    }}
                    title="Regenerate image"
                    content={
                      <div style={{ width: 280 }}>
                        <Input.TextArea
                          rows={2}
                          value={regenFor === item.id ? regenText : ''}
                          onChange={(e) => setRegenText(e.target.value)}
                          placeholder="Optional: how to change it (e.g. warmer tones, add eucalyptus, minimal background)"
                          style={{ marginBottom: 8 }}
                        />
                        <Space>
                          <Button
                            type="primary"
                            size="small"
                            loading={regenImage.isPending && regenImage.variables?.id === item.id}
                            onClick={() =>
                              regenImage.mutate({
                                id: item.id,
                                ...(regenText.trim() ? { instruction: regenText.trim() } : {}),
                              })
                            }
                          >
                            Regenerate
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setRegenFor(null);
                              setRegenText('');
                            }}
                          >
                            Cancel
                          </Button>
                        </Space>
                      </div>
                    }
                  >
                    <Button size="small">
                      {item.imageUrl ? '↻ Regenerate image' : '+ Generate image'}
                    </Button>
                  </Popover>
                </div>
              ) : null}
              <p style={{ marginTop: 0 }}>{item.body}</p>
              {item.hooks.length > 0 ? (
                <p style={{ margin: '4px 0', color: '#6b7280' }}>Hooks: {item.hooks.join(' · ')}</p>
              ) : null}
              {item.cta ? <p style={{ margin: '4px 0' }}>CTA: {item.cta}</p> : null}
              {item.hashtags.length > 0 ? (
                <Space wrap size={4}>
                  {item.hashtags.map((h) => (
                    <Tag key={h} color="blue">
                      {h}
                    </Tag>
                  ))}
                </Space>
              ) : null}
              {item.mediaIdea && !item.imageUrl ? (
                <p style={{ margin: '8px 0 0', color: '#9ca3af', fontStyle: 'italic' }}>
                  Image idea (internal, not published): {item.mediaIdea}
                </p>
              ) : null}
              {item.externalRefs.length > 0 ? (
                <Space wrap size={4} style={{ marginTop: 8 }}>
                  {item.externalRefs.map((r) =>
                    r.url ? (
                      <a key={r.ref} href={r.url} target="_blank" rel="noreferrer">
                        <Tag color="green">Live on {r.channel} ↗</Tag>
                      </a>
                    ) : (
                      <Tag key={r.ref} color="green">
                        Live on {r.channel}
                      </Tag>
                    ),
                  )}
                </Space>
              ) : null}
              {item.publishError ? (
                <p style={{ margin: '8px 0 0', color: '#cf1322' }}>
                  Publish error: {item.publishError}
                </p>
              ) : null}
            </Card>
          ))}
        </Space>
      )}

      <Modal
        title="Edit post"
        open={editItem !== null}
        onCancel={() => setEditItem(null)}
        onOk={() => editText.mutate()}
        okText="Save"
        confirmLoading={editText.isPending}
        okButtonProps={{ disabled: !editForm.title.trim() }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary">Title</Typography.Text>
            <Input
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div>
            <Typography.Text type="secondary">Body (bilingual as generated)</Typography.Text>
            <Input.TextArea
              rows={6}
              value={editForm.body}
              onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
            />
          </div>
          <div>
            <Typography.Text type="secondary">Call to action</Typography.Text>
            <Input
              value={editForm.cta}
              onChange={(e) => setEditForm((f) => ({ ...f, cta: e.target.value }))}
            />
          </div>
          <div>
            <Typography.Text type="secondary">Hashtags (comma-separated)</Typography.Text>
            <Input
              value={editForm.hashtags}
              onChange={(e) => setEditForm((f) => ({ ...f, hashtags: e.target.value }))}
            />
          </div>
        </Space>
      </Modal>
    </Space>
  );
}
